import React, { useState, useEffect } from 'react';
import { fetchShippingToday, fetchPackingSlips, fetchOrderProfit, computePackingCost, savePackingOverride, deletePackingOverride, fetchPackerDay, fetchOrderCosts, saveOrderCosts } from '../services/api';
import { toTitleCase } from '../utils/format';
import VipBadge from '../components/VipBadge';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, DollarSign } from 'lucide-react';

function PackingSlipModal({ internalIds, onClose }) {
  const [slips, setSlips] = useState(null);
  const [idx, setIdx] = useState(0);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPackingSlips(internalIds)
      .then(setSlips)
      .catch(e => setError(e.message));
  }, [internalIds]);

  const slip = slips?.[idx];
  const total = slips?.length ?? 0;

  const addr = (info, ship) => {
    const name = ship ? `${info.shipName ?? ''} ${info.shipLastName ?? ''}`.trim() : `${info.firstName ?? ''} ${info.lastName ?? ''}`.trim();
    const addr1 = ship ? info.shipAddress : info.address;
    const addr2 = ship ? info.shipAddress2 : info.address2;
    const city = ship ? info.shipTown : info.city;
    const state = ship ? info.shipState : info.state;
    const zip = ship ? info.shipZip : info.postCode;
    const phone = info.phone;
    const email = info.customerEmail ?? info.email;
    return { name: toTitleCase(name), addr1, addr2, city: toTitleCase(city), state, zip, phone, email };
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 620, maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Packing Invoice</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {/* toolbar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <button className="btn btn-primary" style={{ fontSize: 12 }} onClick={() => window.print()}>Print All</button>
            <button className="nav-btn" onClick={() => setIdx(0)} disabled={idx === 0}>⇤</button>
            <button className="nav-btn" onClick={() => setIdx(i => Math.max(0, i - 1))} disabled={idx === 0}>‹</button>
            <span style={{ fontSize: 12, fontWeight: 600, minWidth: 24, textAlign: 'center' }}>{total ? idx + 1 : 0}</span>
            <button className="nav-btn" onClick={() => setIdx(i => Math.min(total - 1, i + 1))} disabled={idx >= total - 1}>›</button>
            <button className="nav-btn" onClick={() => setIdx(total - 1)} disabled={idx >= total - 1}>⇥</button>
            <span style={{ fontSize: 12, color: 'var(--gray-500)', marginLeft: 'auto' }}>Showing {total ? idx + 1 : 0} of {total} entries</span>
          </div>

          {error && <div style={{ color: '#c0392b', fontSize: 13 }}>{error}</div>}
          {!slips && !error && <div style={{ textAlign: 'center', color: 'var(--gray-500)', fontSize: 13 }}>Loading...</div>}

          {slip && (() => {
            const o = slip.orderInfo ?? slip.OrderInfo ?? {};
            const c = slip.customerInfo ?? slip.CustomerInfo ?? {};
            const products = slip.products ?? slip.Products ?? [];
            const bill = addr(c, false);
            const ship = addr(c, true);
            return (
              <div style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 13 }}>
                {/* barcode-style order number */}
                <div style={{ textAlign: 'center', fontSize: 32, fontWeight: 900, letterSpacing: 2, marginBottom: 16 }}>
                  *{o.bcOrderId ?? o.BCOrderId ?? o.orderId ?? o.OrderId}*
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
                  <div><strong>Date:</strong> {o.orderDate ?? o.OrderDate}</div>
                  <div><strong>Delivery Date:</strong> {o.deliveryDate ?? o.DeliveryDate ?? '—'}</div>
                  <div><strong>Order ID:</strong> {o.bcOrderId ?? o.BCOrderId}</div>
                </div>

                <hr style={{ borderColor: 'var(--gray-200)', marginBottom: 12 }} />

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 12 }}>
                  {[['Bill To', bill], ['Ship To', ship]].map(([label, a]) => (
                    <div key={label}>
                      <div style={{ fontWeight: 700, marginBottom: 4 }}>{label}:</div>
                      <div>{a.name}</div>
                      <div>{a.addr1}{a.addr2 ? `, ${a.addr2}` : ''}</div>
                      <div>{a.city}, {a.state} {a.zip}</div>
                      {a.phone && <div><strong>Day Phone:</strong> {a.phone}</div>}
                      {label === 'Bill To' && a.email && <a className="text-orange" style={{ fontSize: 12 }}>{a.email}</a>}
                      {label === 'Ship To' && a.email && <a className="text-orange" style={{ fontSize: 12 }}>{a.email}</a>}
                    </div>
                  ))}
                </div>

                {(o.officeNotes ?? o.OfficeNotes) && (
                  <div style={{ background: 'var(--gray-50)', borderRadius: 6, padding: 10, fontSize: 12, marginBottom: 10 }}>
                    <strong>Office Notes:</strong> {o.officeNotes ?? o.OfficeNotes}
                  </div>
                )}

                {o.packingComment && (
                  <div style={{ marginBottom: 10, fontSize: 12 }}>
                    <strong>Packing Notes:</strong> {o.packingComment}
                  </div>
                )}

                {o.giftMessage && (
                  <div style={{ marginBottom: 10, fontSize: 12 }}>
                    <strong>Gift Message:</strong> {o.giftMessage}
                  </div>
                )}

                {products.length > 0 && (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, border: '1px solid #000', marginBottom: 12 }}>
                    <thead>
                      <tr>
                        <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 700, borderBottom: '1px solid #000', borderRight: '1px solid #000' }}>PRODUCT</th>
                        <th style={{ padding: '6px 8px', textAlign: 'center', fontWeight: 700, borderBottom: '1px solid #000' }}>SKU</th>
                      </tr>
                    </thead>
                    <tbody>
                      {products.map((p, i) => {
                        const name = p.name ?? '';
                        const sku = p.sku ?? '';
                        const qty = p.numItems ?? 1;
                        const price = Number(p.unitPrice ?? 0);
                        const features = p.features ?? [];
                        return (
                          <tr key={i} style={{ borderBottom: '1px solid #ccc' }}>
                            <td style={{ padding: '8px', borderRight: '1px solid #000' }}>
                              <div style={{ fontWeight: 600, marginBottom: 4 }}>{name}</div>
                              {features.map((f, fi) => (
                                <div key={fi} style={{ fontSize: 12 }}>
                                  {f.feature ?? f.Feature}:: <strong style={{ fontSize: 14 }}>{f.value ?? f.Value}</strong>
                                </div>
                              ))}
                            </td>
                            <td style={{ padding: '8px', textAlign: 'center', verticalAlign: 'middle' }}>
                              {sku && <div style={{ fontSize: 20, fontWeight: 900, letterSpacing: 1 }}>*{sku}*</div>}
                              <div style={{ marginTop: 4 }}>${price.toFixed(2)}</div>
                            </td>
                          </tr>
                        );
                      })}
                      <tr style={{ borderBottom: '1px solid #ccc' }}>
                        <td style={{ padding: '8px', borderRight: '1px solid #000' }}>
                          <strong>Total Number of Packing Items: {products.reduce((s, p) => s + (p.numItems ?? 1), 0)}</strong>
                        </td>
                        <td></td>
                      </tr>
                      {o.shipMethod && (
                        <tr style={{ borderBottom: '1px solid #ccc' }}>
                          <td style={{ padding: '8px', borderRight: '1px solid #000' }}>
                            Shipping Method: <strong>{o.shipMethod}</strong>
                          </td>
                          <td style={{ padding: '8px', textAlign: 'center' }}>${o.shipCost.toFixed(2)}</td>
                        </tr>
                      )}
                      <tr>
                        <td style={{ padding: '8px', borderRight: '1px solid #000' }}><strong>Total:</strong></td>
                        <td style={{ padding: '8px', textAlign: 'center' }}><strong>${o.orderTotal.toFixed(2)}</strong></td>
                      </tr>
                    </tbody>
                  </table>
                )}

                {/* fill-in section */}
                <div style={{ border: '1px solid #000', borderRadius: 4, padding: 12, fontSize: 13, lineHeight: 2 }}>
                  <div>Box:______________ # Gel Packs: ______________ # Scoops</div>
                  <div>Dry Ice: ______________</div>
                  <div>Crab Steamed Date:{new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}</div>
                  <div style={{ marginTop: 4 }}>Packing Comments:</div>
                  <div style={{ height: 48 }}></div>
                </div>

                <div style={{ marginTop: 16, textAlign: 'left' }}>
                  <img src="https://www.ups.com/assets/resources/webcontent/images/ups-logo.svg" alt="UPS" style={{ height: 56 }} />
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
}

const normBox = (s) => String(s || '').toUpperCase().replace(/\s*\(.*$/, '').trim();

const VipTag = ({ notRenewing }) => (
  <VipBadge notRenewing={notRenewing} style={{ marginLeft: 6, fontSize: 9, fontWeight: 700, background: 'var(--orange)', color: '#fff', borderRadius: 4, padding: '1px 5px', verticalAlign: 'middle' }} />
);

// Our-cost fields on the order, the same ones the live admin's Order Profit window edits.
// `when` names the charge that has to be on the order for the row to show at all.
const COST_ROWS = [
  { field: 'packOurCost', label: () => 'Packing Materials', charge: 'packCost' },
  { field: 'shipOurCost', label: (pi) => pi.shipMethodType || 'Shipping', charge: 'shipCost' },
  { field: 'adjustOurCost', label: (pi) => `Adjustment${pi.adjustmentDescription ? ` (${pi.adjustmentDescription})` : ''}`, charge: 'adjustment' },
  { field: 'satOurCost', label: () => 'Saturday Delivery', charge: 'satSurcharge', when: 'satSurcharge' },
  { field: 'hiakOurCost', label: () => 'Hawaii/Alaska Delivery', charge: 'hiakSurcharge', when: 'hiakSurcharge' },
  { field: 'couponOurCost', label: (pi) => `Coupon${pi.coupon ? `: ${pi.coupon}` : ''}`, charge: 'couponDiscount', when: 'couponDiscount' },
  { field: 'giftOurCost', label: (pi) => `Gift Certificate${pi.giftCertificate ? `: ${pi.giftCertificate}` : ''}`, charge: 'giftAmountUsed', when: 'giftAmountUsed' },
];
const cents = (v) => (Number(v) || 0).toFixed(2);
const costDraft = (raw) => ({
  raw,
  items: (raw.products || []).map(p => cents(p.ourCost)),
  info: Object.fromEntries(COST_ROWS.map(r => [r.field, cents((raw.productInfo || {})[r.field])])),
});

function OrderProfitModal({ order, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [qty, setQty] = useState(null);         // editable { boxType, gelPacks, dryIce }
  const [baseline, setBaseline] = useState(null); // last-saved (or loaded) packing qty
  const [hasOverride, setHasOverride] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);
  const [costs, setCosts] = useState(null);     // editable our-costs: { raw, items[], info{} }
  const [savingCosts, setSavingCosts] = useState(false);

  useEffect(() => {
    let alive = true;
    setData(null); setError(null); setQty(null); setSaveMsg(null); setCosts(null);
    // If this read fails the window still opens, with costs shown but not editable.
    fetchOrderCosts(order.internalId)
      .then(raw => { if (alive && raw && raw.productInfo) setCosts(costDraft(raw)); })
      .catch(() => {});
    fetchOrderProfit(order.internalId)
      .then(d => { if (!alive) return; setData(d); setQty(d.effPackQty); setBaseline(d.effPackQty); setHasOverride(!!d.override); })
      .catch(e => { if (alive) setError(e.message); });
    return () => { alive = false; };
  }, [order.internalId]);

  const money = (v) => `${v < 0 ? '-' : ''}$${Math.abs(Number(v || 0)).toFixed(2)}`;
  const addrLines = (a) => [a.line1, [a.city, a.state].filter(Boolean).join(', ') + (a.zip ? ` ${a.zip}` : '')].filter(s => s && s.trim());
  const num = (v) => Number(v || 0);

  const setQ = (patch) => { setQty(q => ({ ...q, ...patch })); setSaveMsg(null); };
  const sameQty = (a, b) => a && b && normBox(a.boxType) === normBox(b.boxType) && num(a.gelPacks) === num(b.gelPacks) && num(a.dryIce) === num(b.dryIce);
  const dirty = qty && baseline && !sameQty(qty, baseline);
  const overridden = qty && data && !sameQty(qty, data.prodPackQty);

  const doSave = async () => {
    setSaving(true); setSaveMsg(null);
    try {
      await savePackingOverride(data.orderId, qty);
      setBaseline(qty); setHasOverride(true); setSaveMsg({ type: 'ok', msg: 'Saved to CPx' });
    } catch (e) { setSaveMsg({ type: 'error', msg: 'Save failed: ' + e.message }); }
    finally { setSaving(false); }
  };
  const doReset = async () => {
    setSaving(true); setSaveMsg(null);
    try {
      if (hasOverride) await deletePackingOverride(data.orderId);
      setQty(data.prodPackQty); setBaseline(data.prodPackQty); setHasOverride(false);
      setSaveMsg({ type: 'ok', msg: 'Reset to production' });
    } catch (e) { setSaveMsg({ type: 'error', msg: 'Reset failed: ' + e.message }); }
    finally { setSaving(false); }
  };

  // Live packing + profit math from the current (editable) quantities.
  const pb = data && qty && data.packConfig ? computePackingCost(data.packConfig, qty) : null;
  const boxOptions = (() => {
    const opts = (data?.packConfig?.boxes || []).map(b => b.boxType).filter(Boolean);
    const cur = normBox(qty?.boxType);
    if (cur && cur !== 'NO BOX' && !opts.includes(cur)) opts.push(cur);
    return opts;
  })();
  const packingLines = pb ? [
    { label: `Box${qty.boxType ? ` (${normBox(qty.boxType) || qty.boxType})` : ''}${pb.missingBox ? ' — set cost in Packing Costs' : ''}`, cost: pb.box },
    ...(num(qty.gelPacks) ? [{ label: `Gel Packs (${num(qty.gelPacks)} × $${pb.gelUnit.toFixed(3)})`, cost: pb.gel }] : []),
    ...(num(qty.dryIce) ? [{ label: `Dry Ice (${num(qty.dryIce)} lb × $${pb.dryRate.toFixed(2)})`, cost: pb.dryice }] : []),
  ] : [];
  const totalCost = data ? data.productCost + (pb ? pb.total : (data.savedPackingCost || 0)) + data.otherCost : 0;

  // Our costs, editable. Totals start from the server's own figures and move by
  // whatever has been typed, so an untouched order shows exactly what the live admin shows.
  const cpi = costs ? (costs.raw.productInfo || {}) : {};
  const coi = costs ? (costs.raw.orderInfo || {}) : {};
  const costRows = costs ? COST_ROWS.filter(r => !r.when || num(cpi[r.when])) : [];
  const costDelta = costs
    ? (costs.raw.products || []).reduce((t, p, i) => t + (num(costs.items[i]) - num(cents(p.ourCost))), 0)
      + costRows.reduce((t, r) => t + (num(costs.info[r.field]) - num(cents(cpi[r.field]))), 0)
    : 0;
  const costsDirty = !!costs && ((costs.raw.products || []).some((p, i) => cents(costs.items[i]) !== cents(p.ourCost))
    || costRows.some(r => cents(costs.info[r.field]) !== cents(cpi[r.field])));
  const serverTotals = !!costs && coi.totalOurCosts != null && Number.isFinite(Number(coi.totalOurCosts)) && Number(coi.totalRevenue) > 0;
  const shownCost = serverTotals ? Number(coi.totalOurCosts) + costDelta : totalCost;
  const shownRevenue = serverTotals ? Number(coi.totalRevenue) : (data ? data.orderAmount : 0);
  const shownProfit = shownRevenue - shownCost;
  const shownMargin = shownRevenue ? (shownProfit / shownRevenue) * 100 : 0;
  const setItemCost = (i, v) => { setCosts(c => ({ ...c, items: c.items.map((x, k) => (k === i ? v : x)) })); setSaveMsg(null); };
  const setInfoCost = (f, v) => { setCosts(c => ({ ...c, info: { ...c.info, [f]: v } })); setSaveMsg(null); };

  const doSaveCosts = async () => {
    setSavingCosts(true); setSaveMsg(null);
    try {
      const products = (costs.raw.products || []).map((p, i) => ({ ...p, ourCost: num(costs.items[i]) }));
      const productInfo = { ...cpi };
      costRows.forEach(r => { productInfo[r.field] = num(costs.info[r.field]); });
      await saveOrderCosts(order.internalId, products, productInfo);
      // Read it back so the totals on screen are the order's, not our arithmetic.
      const fresh = await fetchOrderCosts(order.internalId);
      if (fresh && fresh.productInfo) setCosts(costDraft(fresh));
      setSaveMsg({ type: 'ok', msg: 'Costs saved' });
    } catch (e) { setSaveMsg({ type: 'error', msg: 'Costs not saved: ' + e.message }); }
    finally { setSavingCosts(false); }
  };

  const costInput = { width: 84, textAlign: 'right', border: '1px solid var(--gray-300)', borderRadius: 4, padding: '4px 6px', fontFamily: 'Montserrat', fontSize: 12 };
  const packInput = { width: 64, textAlign: 'right', border: '1px solid var(--gray-300)', borderRadius: 4, padding: '3px 6px', fontFamily: 'Montserrat', fontSize: 12 };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Order Profit</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {!data && !error && (
            <div style={{ padding: 24, textAlign: 'center', fontSize: 13, color: 'var(--gray-500)' }}>Loading profit details…</div>
          )}
          {error && (
            <div style={{ padding: 16, fontSize: 13, color: '#c0392b', background: '#fff5f5', border: '1px solid #fcc', borderRadius: 6 }}>
              <strong>Couldn't load order:</strong> {error}
            </div>
          )}
          {data && qty && (<>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16, fontSize: 12 }}>
            <div><strong>Order ID:</strong> {data.bcOrderId || order.id}</div>
            <div><strong>Delivery Date:</strong> {data.deliveryDate || '—'}</div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 12, fontSize: 12 }}>
            <div>
              <div style={{ fontWeight: 700, marginBottom: 4, color: 'var(--navy)' }}>Bill To:</div>
              <div>{toTitleCase(data.billTo.name) || '—'}{order.isVip && <VipTag notRenewing={order.vipNotRenewing} />}</div>
              {addrLines(data.billTo).map((l, i) => <div key={i} style={{ color: 'var(--gray-500)' }}>{toTitleCase(l)}</div>)}
              {data.billTo.phone && <div style={{ color: 'var(--gray-500)' }}>Day Phone: {data.billTo.phone}</div>}
              {data.billTo.email && <a className="text-orange" style={{ fontSize: 11 }}>{data.billTo.email}</a>}
            </div>
            <div>
              <div style={{ fontWeight: 700, marginBottom: 4, color: 'var(--navy)' }}>Ship To:</div>
              <div>{toTitleCase(data.shipTo.name) || toTitleCase(data.billTo.name) || '—'}{order.isVip && <VipTag notRenewing={order.vipNotRenewing} />}</div>
              {addrLines(data.shipTo).map((l, i) => <div key={i} style={{ color: 'var(--gray-500)' }}>{toTitleCase(l)}</div>)}
              {data.shipTo.phone && <div style={{ color: 'var(--gray-500)' }}>Day Phone: {data.shipTo.phone}</div>}
            </div>
          </div>

          {data.officeNotes && (
            <div style={{ background: 'var(--gray-50)', borderRadius: 6, padding: 12, fontSize: 11.5, color: 'var(--gray-700)', marginBottom: 12 }}>
              <strong>Office Notes:</strong> {data.officeNotes}
            </div>
          )}

          {/* Packing strip — Box / Gel / Dry Ice are editable (CPx-side, per order) */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--navy)' }}>Packing</span>
            {overridden && <span style={{ fontSize: 9, fontWeight: 700, background: 'var(--peach)', color: 'var(--navy)', borderRadius: 4, padding: '1px 6px' }}>EDITED (CPx)</span>}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 12, marginBottom: 12, alignItems: 'end' }}>
            <div style={{ fontSize: 11 }}><div style={{ color: 'var(--gray-500)', fontWeight: 600 }}>Crab Steamed</div><div style={{ fontWeight: 700, color: 'var(--navy)' }}>{data.packingInfo.steamedDate || '—'}</div></div>
            <div style={{ fontSize: 11 }}>
              <div style={{ color: 'var(--gray-500)', fontWeight: 600 }}>Box Type</div>
              <select value={boxOptions.includes(normBox(qty.boxType)) ? normBox(qty.boxType) : (normBox(qty.boxType) === 'NO BOX' ? 'NO BOX' : (qty.boxType ? normBox(qty.boxType) : ''))}
                onChange={e => setQ({ boxType: e.target.value })}
                style={{ ...packInput, width: '100%', textAlign: 'left' }}>
                <option value="">—</option>
                <option value="NO BOX">NO BOX</option>
                {boxOptions.map(bt => <option key={bt} value={bt}>{bt}</option>)}
              </select>
            </div>
            <div style={{ fontSize: 11 }}>
              <div style={{ color: 'var(--gray-500)', fontWeight: 600 }}>Gel Packs</div>
              <input type="number" min="0" value={qty.gelPacks ?? 0} onChange={e => setQ({ gelPacks: e.target.value })} style={{ ...packInput, width: '100%' }} />
            </div>
            <div style={{ fontSize: 11 }}>
              <div style={{ color: 'var(--gray-500)', fontWeight: 600 }}>Dry Ice (lb)</div>
              <input type="number" min="0" step="0.5" value={qty.dryIce ?? 0} onChange={e => setQ({ dryIce: e.target.value })} style={{ ...packInput, width: '100%' }} />
            </div>
            <div style={{ fontSize: 11 }}><div style={{ color: 'var(--gray-500)', fontWeight: 600 }}>Packer</div><div style={{ fontWeight: 700, color: 'var(--navy)' }}>{data.packingInfo.packer || '—'}</div></div>
            <div style={{ fontSize: 11 }}><div style={{ color: 'var(--gray-500)', fontWeight: 600 }}>Crab Count</div><div style={{ fontWeight: 700, color: 'var(--navy)' }}>{data.packingInfo.crabCount ?? '—'}</div></div>
          </div>

          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginBottom: 12 }}>
            <thead>
              <tr style={{ background: 'var(--gray-50)' }}>
                <th style={{ padding: '7px 10px', textAlign: 'left', color: 'var(--gray-500)', fontSize: 11, fontWeight: 700 }}>Qty</th>
                <th style={{ padding: '7px 10px', textAlign: 'left', color: 'var(--gray-500)', fontSize: 11, fontWeight: 700 }}>Item</th>
                <th style={{ padding: '7px 10px', textAlign: 'right', color: 'var(--gray-500)', fontSize: 11, fontWeight: 700 }}>Price</th>
                <th style={{ padding: '7px 10px', textAlign: 'right', color: 'var(--gray-500)', fontSize: 11, fontWeight: 700 }}>SubTotal</th>
                <th style={{ padding: '7px 10px', textAlign: 'right', color: 'var(--gray-500)', fontSize: 11, fontWeight: 700 }}>{costs ? 'Cost Per Item' : 'Cost'}</th>
              </tr>
            </thead>
            <tbody>
              {costs && (<>
                {(costs.raw.products || []).map((p, i) => (
                  <tr key={`e${i}`} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                    <td style={{ padding: '8px 10px' }}>{p.numItems}</td>
                    <td style={{ padding: '8px 10px' }}>
                      <div style={{ color: 'var(--orange)', fontWeight: 600 }}>{String(p.externalName || p.name || '').replace(/<br\s*\/?>/gi, ' ')}</div>
                      {(p.features || []).length > 0 && <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>{p.features.map(f => `${f.feature || ''} ${f.value || ''}`.trim()).filter(Boolean).join(' | ')}</div>}
                    </td>
                    <td style={{ padding: '8px 10px', textAlign: 'right' }}>{money(p.unitPrice)}</td>
                    <td style={{ padding: '8px 10px', textAlign: 'right' }}>{money(num(p.unitPrice) * num(p.numItems))}</td>
                    <td style={{ padding: '6px 10px', textAlign: 'right' }}>
                      <input type="number" step="0.01" aria-label={`Cost for ${p.externalName || p.name || 'item'}`} value={costs.items[i]} onChange={e => setItemCost(i, e.target.value)} style={costInput} />
                    </td>
                  </tr>
                ))}
                {costRows.map(r => (
                  <tr key={r.field} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                    <td style={{ padding: '6px 10px' }}></td>
                    <td style={{ padding: '6px 10px', color: 'var(--gray-700)' }}>
                      {r.label(cpi)}
                      {r.field === 'packOurCost' && pb && pb.total > 0 && Math.abs(pb.total - num(costs.info.packOurCost)) > 0.005 && (
                        <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>
                          Box, gel and dry ice above come to {money(pb.total)}.{' '}
                          <button type="button" className="text-orange" style={{ cursor: 'pointer', fontWeight: 600, background: 'none', border: 'none', padding: 0, font: 'inherit' }} onClick={() => setInfoCost('packOurCost', cents(pb.total))}>Use that</button>
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '6px 10px', textAlign: 'right' }}></td>
                    <td style={{ padding: '6px 10px', textAlign: 'right' }}>{money(cpi[r.charge])}</td>
                    <td style={{ padding: '6px 10px', textAlign: 'right' }}>
                      <input type="number" step="0.01" aria-label={`Our cost: ${r.label(cpi)}`} value={costs.info[r.field]} onChange={e => setInfoCost(r.field, e.target.value)} style={costInput} />
                    </td>
                  </tr>
                ))}
                {num(cpi.bcManualDiscount) !== 0 && (
                  <tr style={{ borderBottom: '1px solid var(--gray-100)' }}>
                    <td></td>
                    <td style={{ padding: '6px 10px', color: 'var(--gray-700)' }}>Manual Discount</td>
                    <td></td>
                    <td style={{ padding: '6px 10px', textAlign: 'right' }}>{money(cpi.bcManualDiscount)}</td>
                    <td></td>
                  </tr>
                )}
                {cpi.merchantFees != null && (
                  <tr style={{ borderBottom: '1px solid var(--gray-100)' }}>
                    <td></td>
                    <td style={{ padding: '6px 10px', color: 'var(--gray-700)' }}>Merchant Fees</td>
                    <td></td>
                    <td></td>
                    <td style={{ padding: '6px 17px 6px 10px', textAlign: 'right', color: 'var(--gray-700)' }}>{money(cpi.merchantFees)}</td>
                  </tr>
                )}
              </>)}
              {!costs && data.productLines.map((p, i) => (
                <tr key={i} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                  <td style={{ padding: '8px 10px' }}>{p.qty}</td>
                  <td style={{ padding: '8px 10px' }}>
                    <div style={{ color: 'var(--orange)', fontWeight: 600 }}>{p.name}</div>
                    {p.options && <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>{p.options}</div>}
                  </td>
                  <td style={{ padding: '8px 10px', textAlign: 'right' }}>{money(p.price)}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right' }}>{money(p.subTotal)}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', color: 'var(--gray-700)' }}>{money(p.cost)}</td>
                </tr>
              ))}
              {!costs && packingLines.map((item, i) => (
                <tr key={`p${i}`} style={{ borderBottom: '1px solid var(--gray-100)', background: 'rgba(252,210,153,0.15)' }}>
                  <td style={{ padding: '6px 10px' }}></td>
                  <td style={{ padding: '6px 10px', color: 'var(--gray-700)' }}>{item.label}</td>
                  <td style={{ padding: '6px 10px', textAlign: 'right' }}>$0.00</td>
                  <td style={{ padding: '6px 10px', textAlign: 'right' }}></td>
                  <td style={{ padding: '6px 10px', textAlign: 'right', color: 'var(--gray-700)' }}>{money(item.cost)}</td>
                </tr>
              ))}
              {!costs && data.otherCostLines.map((item, i) => (
                <tr key={`c${i}`} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                  <td style={{ padding: '6px 10px' }}></td>
                  <td style={{ padding: '6px 10px', color: 'var(--gray-700)' }}>{item.label}</td>
                  <td style={{ padding: '6px 10px', textAlign: 'right' }}>{item.charge ? money(item.charge) : '$0.00'}</td>
                  <td style={{ padding: '6px 10px', textAlign: 'right' }}></td>
                  <td style={{ padding: '6px 10px', textAlign: 'right', color: 'var(--gray-700)' }}>{money(item.cost)}</td>
                </tr>
              ))}
              <tr style={{ fontWeight: 700, background: 'var(--gray-50)' }}>
                <td colSpan={3} style={{ padding: '8px 10px', textAlign: 'right', color: 'var(--navy)' }}>Totals:</td>
                <td style={{ padding: '8px 10px', textAlign: 'right', color: 'var(--navy)' }}>{money(serverTotals ? shownRevenue : data.netSubtotal)}</td>
                <td style={{ padding: '8px 10px', textAlign: 'right', color: 'var(--orange)' }}>{money(shownCost)}</td>
              </tr>
            </tbody>
          </table>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 24, alignItems: 'baseline', padding: '10px 12px', background: 'var(--gray-50)', borderRadius: 6, marginBottom: 8, fontSize: 13 }}>
            <div><span style={{ color: 'var(--gray-500)' }}>Revenue </span><strong>{money(shownRevenue)}</strong></div>
            <div><span style={{ color: 'var(--gray-500)' }}>Cost </span><strong>{money(shownCost)}</strong></div>
            <div>
              <span style={{ color: 'var(--gray-500)' }}>Profit </span>
              <strong style={{ color: shownProfit >= 0 ? '#1e7e34' : '#c0392b', fontSize: 15 }}>{money(shownProfit)}</strong>
              <span style={{ color: 'var(--gray-500)', marginLeft: 6 }}>({shownMargin.toFixed(1)}%)</span>
            </div>
          </div>

          {!costs && pb && Math.abs((pb.total || 0) - (data.savedPackingCost || 0)) > 0.005 && (
            <div style={{ fontSize: 11, color: 'var(--gray-500)', textAlign: 'right', marginBottom: 12 }}>
              Packing (WOS): {money(pb.total)} &nbsp;·&nbsp; saved on order: {money(data.savedPackingCost)}
            </div>
          )}

          {data.packingComment && (
            <div>
              <label className="form-label">Packing Comments</label>
              <div style={{ fontSize: 12, color: 'var(--gray-700)', whiteSpace: 'pre-wrap', border: '1px solid var(--gray-200)', borderRadius: 6, padding: 10, background: '#fff' }}>{data.packingComment}</div>
            </div>
          )}
          </>)}
        </div>
        <div className="modal-footer" style={{ alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 11, color: 'var(--gray-500)', marginRight: 'auto' }}>
            {saveMsg
              ? <span style={{ fontWeight: 600, color: saveMsg.type === 'error' ? '#c0392b' : '#1e7e34' }}>{saveMsg.msg}</span>
              : costs
                ? 'Save costs writes to the order, the same as the other admin. Box, gel and dry ice stay in CPx.'
                : 'Packing edits save to CPx only (not written back to production).'}
          </span>
          {data && (hasOverride || overridden) && (
            <button className="btn btn-ghost" onClick={doReset} disabled={saving} style={{ fontSize: 12 }}>Reset to production</button>
          )}
          <button className={costs ? 'btn btn-outline' : 'btn btn-navy'} onClick={doSave} disabled={saving || !dirty}>{saving ? 'Saving…' : 'Save packing'}</button>
          {costs && <button className="btn btn-navy" onClick={doSaveCosts} disabled={savingCosts || !costsDirty}>{savingCosts ? 'Saving…' : 'Save costs'}</button>}
        </div>
      </div>
    </div>
  );
}

// Where each packer is for the day, across the top of the queue (Carman,
// 2026-09-02). One tile per packer: orders packed, and their rate once there
// are enough hours behind it to mean anything.
//
// ⚠️ NOBODY WITH ORDERS IS EVER DROPPED. A packer who has started but has not
// clocked into the Packing job code yet shows their count with no rate, and a
// set of initials with no person against it shows the initials — the floor
// knows who "hp" is even when our identity table does not. Dropping either is
// the exact failure Carman named when the TV board was built.
function PackerDayStrip({ day }) {
  if (!day || !day.available || !day.packers || !day.packers.length) return null;

  const packers = day.packers;
  const best = Math.max(...packers.map(p => p.orders || 0));

  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--navy)' }}>Packed today by</span>
        <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>
          {day.attributed} of {day.packedTotal} packed orders
          {/* An order packed but not yet traced to a packer is a real state, not a
              rounding error. Saying so keeps the tiles adding up to the queue's
              own packed count instead of quietly falling short of it. */}
          {day.unattributed > 0 && <> · {day.unattributed} not yet attributed</>}
        </span>
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {packers.map(p => (
          <div key={p.raw} style={{
            border: `1px solid ${p.orders === best ? 'var(--orange)' : 'var(--gray-200)'}`,
            background: p.orders === best ? 'rgba(252,210,153,0.18)' : '#fff',
            borderRadius: 8, padding: '7px 12px', minWidth: 108,
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--navy)', display: 'flex', alignItems: 'center', gap: 5 }}>
              {p.name}
              {!p.mapped && (
                <span title="These initials are not mapped to a person yet" style={{ fontSize: 8, fontWeight: 700, background: 'var(--orange)', color: '#fff', borderRadius: 3, padding: '1px 4px' }}>?</span>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 2 }}>
              <span style={{ fontSize: 19, fontWeight: 800, color: 'var(--navy)', lineHeight: 1.1 }}>{p.orders}</span>
              <span style={{ fontSize: 10, color: 'var(--gray-500)' }}>orders</span>
            </div>
            <div style={{ fontSize: 10, color: 'var(--gray-500)', marginTop: 1 }}>
              {typeof p.oph === 'number' && p.oph > 0
                ? <><strong style={{ color: 'var(--navy)' }}>{p.oph}</strong> OPH</>
                : (p.rateState === 'no-hours' ? 'not clocked in' : 'rate too early')}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ShippingToday({ navigateTo, initialDate }) {
  const [shippingOrders, setShippingOrders] = useState(null);
  const [shippingHeader, setShippingHeader] = useState(null);
  const [error, setError] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [checkedIds, setCheckedIds] = useState(new Set());
  const [packingSlipIds, setPackingSlipIds] = useState(null);
  const [vipFilter, setVipFilter] = useState(false);
  const [packerDay, setPackerDay] = useState(null);

  const [date, setDate] = useState(() => initialDate ? new Date(initialDate) : new Date());

  const fmtDate = (d) => {
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${mm}/${dd}/${d.getFullYear()}`;
  };
  const shippingDate = `${fmtDate(date)} - ${fmtDate(date)}`;

  const shiftDate = (days) => setDate(d => {
    const next = new Date(d);
    next.setDate(next.getDate() + days);
    return next;
  });

  useEffect(() => {
    setShippingOrders(null);
    setShippingHeader(null);
    setError(null);
    fetchShippingToday(fmtDate(date))
      .then(result => { setShippingOrders(result.orders); setShippingHeader(result.header); })
      .catch(e => setError(e.message));

    // Who has packed what, for the strip above the queue. Deliberately its own
    // request and never awaited alongside the queue: it is a convenience, and a
    // failure here must cost the strip, not the shipping list. fetchPackerDay
    // resolves null rather than throwing, so there is nothing to catch.
    //
    // The date nav goes back as far as anyone wants, but this reads the cached
    // shipping report for the day, and that is only warmed for today — an older
    // day comes back available:false and the strip renders nothing rather than
    // an error nobody can act on.
    let cancelled = false;
    const ymd = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    setPackerDay(null);
    fetchPackerDay(ymd).then(d => { if (!cancelled) setPackerDay(d); });
    return () => { cancelled = true; };
  }, [date]);

  // Prefer production's header totals (exact match to the live admin); fall back
  // to client-side counts if the header is ever missing.
  const h = shippingHeader;
  const totalOrders = h?.totalOrders ?? shippingOrders?.length ?? 0;
  const newOrders = h?.newOrders ?? shippingOrders?.filter(o => !o.reship).length ?? 0;
  const reships = h?.reshipOrders ?? shippingOrders?.filter(o => o.reship).length ?? 0;
  const packedCount = h?.packedOrders ?? shippingOrders?.filter(o => o.done).length ?? 0;
  const remainingCount = h?.remainingOrders ?? (totalOrders - packedCount);
  const totalRevenue = h?.totalSales ?? shippingOrders?.reduce((sum, o) => sum + parseFloat(o.total.replace('$', '') || 0), 0) ?? 0;
  const totalProfit = h?.totalProfit ?? shippingOrders?.reduce((sum, o) => sum + o.profit, 0) ?? 0;
  const avgProfit = h?.averageProfit ?? (totalOrders ? totalProfit / totalOrders : 0);
  const ordersToPrint = h?.ordersToPrint ?? 0;

  const getProfitClass = (p) => p >= 20 ? 'profit-positive' : p >= 0 ? 'profit-low' : 'profit-negative';

  return (
    <div>
      <div className="breadcrumb">
        <a onClick={() => navigateTo('dashboard')}>Dashboard</a>
        <span>›</span>
        <span>Orders Shipping Today</span>
      </div>
      <div className="page-header">Orders Shipping Today</div>
      <div style={{ marginBottom: 16 }} />

      <div className="shipping-header-bar">
        <div className="shipping-date-nav">
          <button className="nav-btn" onClick={() => shiftDate(-7)}><ChevronsLeft size={13} /></button>
          <button className="nav-btn" onClick={() => shiftDate(-1)}><ChevronLeft size={13} /></button>
          <span className="shipping-date">{shippingDate}</span>
          <button className="nav-btn" onClick={() => shiftDate(1)}><ChevronRight size={13} /></button>
          <button className="nav-btn" onClick={() => shiftDate(7)}><ChevronsRight size={13} /></button>
        </div>
        <div className="shipping-summary">
          <strong>{totalOrders} Orders</strong> ({newOrders} New / {reships} Reship) &nbsp;|&nbsp;
          {remainingCount} Remaining<br />
          <strong>{packedCount} Packed</strong> &nbsp;(Total Sales – <strong>${totalRevenue.toFixed(2)}</strong> / Total Profit – <strong>${totalProfit.toFixed(2)}</strong> / Ave Profit Per Order – <strong>${avgProfit.toFixed(2)}</strong>)<br />
          <a className="text-orange" style={{ cursor: 'pointer', fontWeight: 700 }}>{ordersToPrint} New Orders To Print</a>
        </div>
      </div>

      <PackerDayStrip day={packerDay} />

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12, alignItems: 'center' }}>
        {['Select Active VIPs', 'Select Orders Not Done', 'Select Orders Not Printed', 'Charge Orders', 'Product List', 'Packing Slips', 'Invoices', 'Prep Instructions', 'End of Day Report'].map((link, i, arr) => (
          <React.Fragment key={link}>
            <a className="shipping-action-link" style={link === 'Select Active VIPs' && vipFilter ? { color: 'var(--orange)', fontWeight: 700 } : {}} onClick={() => {
              if (link === 'Select Orders Not Done') {
                setCheckedIds(new Set(shippingOrders?.filter(o => !o.costsSaved).map(o => o.id) ?? []));
              } else if (link === 'Select Orders Not Printed') {
                setCheckedIds(new Set(shippingOrders?.filter(o => !o.printed).map(o => o.id) ?? []));
              } else if (link === 'Select Active VIPs') {
                setVipFilter(f => !f);
                setCheckedIds(new Set(shippingOrders?.filter(o => o.isVip).map(o => o.id) ?? []));
              } else if (link === 'Packing Slips') {
                const ids = shippingOrders?.filter(o => checkedIds.has(o.id)).map(o => o.internalId) ?? [];
                if (ids.length > 0) setPackingSlipIds(ids);
              }
            }}>{link}</a>
            {i < arr.length - 1 && <span className="shipping-action-sep">|</span>}
          </React.Fragment>
        ))}
      </div>

      {error && (
        <div style={{ padding: 16, fontSize: 13, color: '#c0392b', background: '#fff5f5', border: '1px solid #fcc', borderRadius: 6, marginBottom: 12 }}>
          <strong>Error loading orders:</strong> {error}
        </div>
      )}

      {!shippingOrders && !error && (
        <div style={{ padding: 24, textAlign: 'center', fontSize: 13, color: 'var(--gray-500)' }}>Loading orders...</div>
      )}

      {shippingOrders && (
        <table className="orders-table">
          <thead>
            <tr>
              <th style={{ width: 30 }}><input type="checkbox" onChange={e => setCheckedIds(e.target.checked ? new Set(shippingOrders.map(o => o.id)) : new Set())} /></th>
              <th>Order ID</th>
              <th style={{ width: 50 }}>Done</th>
              <th>Name (Bill To)</th>
              <th>Ship Type</th>
              <th>Order Total</th>
              <th>Profit</th>
            </tr>
          </thead>
          <tbody>
            {shippingOrders.filter(o => !vipFilter || o.isVip).map(order => (
              <tr key={order.id}>
                <td><input type="checkbox" checked={checkedIds.has(order.id)} onChange={e => {
                    const next = new Set(checkedIds);
                    e.target.checked ? next.add(order.id) : next.delete(order.id);
                    setCheckedIds(next);
                  }} /></td>
                <td>
                  <a className="order-link" onClick={() => navigateTo('order-details', order.id)}>
                    {order.id}{order.reship ? ' - (Reship)' : ''}
                  </a>
                </td>
                <td>{order.done && <span className="done-check">✓</span>}</td>
                <td>
                  <div style={{ fontWeight: 600 }}>{toTitleCase(order.name)}{order.isVip && <VipTag notRenewing={order.vipNotRenewing} />}</div>
                  <div className="order-sub-links">
                    <a className="order-sub-link">Invoice</a>
                    <span style={{ color: 'var(--gray-300)' }}>|</span>
                    <a className="order-sub-link" onClick={() => setPackingSlipIds([order.internalId])}>Reprint Packing Slip</a>
                    <span style={{ color: 'var(--gray-300)' }}>|</span>
                    <a className="order-sub-link">Prep Instructions</a>
                  </div>
                </td>
                <td>{order.shipType}</td>
                <td style={{ fontWeight: 600 }}>{order.total}</td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
                    {order.costsSaved && (
                      <span style={{ fontSize: 9, fontWeight: 700, background: 'var(--orange)', color: '#fff', borderRadius: 4, padding: '2px 6px', letterSpacing: 0.3 }}>DONE</span>
                    )}
                    <span className={`profit-badge ${getProfitClass(order.profit)}`} onClick={() => setSelectedOrder(order)} title="Click to view profit details">
                      {order.profit >= 0 ? '+' : ''}${order.profit.toFixed(2)}
                    </span>
                    <span
                      onClick={() => setSelectedOrder(order)}
                      title={order.costsSaved ? 'Done — costs saved' : 'Not done — costs not saved yet'}
                      style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 26, height: 26, borderRadius: '50%', border: `1.5px solid ${order.costsSaved ? 'var(--orange)' : 'var(--gray-400)'}`, background: order.costsSaved ? 'var(--orange)' : '#fff' }}
                    >
                      <DollarSign size={15} strokeWidth={2.75} color={order.costsSaved ? '#fff' : 'var(--gray-500)'} />
                    </span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {selectedOrder && <OrderProfitModal order={selectedOrder} onClose={() => setSelectedOrder(null)} />}
      {packingSlipIds && <PackingSlipModal internalIds={packingSlipIds} onClose={() => setPackingSlipIds(null)} />}
    </div>
  );
}
